/**
 * MedPass Workflow Service
 *
 * This service handles the 4-step MedPass electronic prescription workflow,
 * including patient demographics, medication order entry, pre/post conditions,
 * and physician notes with scheduling.
 *
 * Enhanced with smart medication dashboard functionality, two-timestamp tracking,
 * and industry-standard status management.
 */

import apiClient from './apiClient';

class MedPassService {
    constructor() {
        this.baseURL = '/medpass';
    }

    // ============================================================================
    // WORKFLOW MANAGEMENT
    // ============================================================================

    /**
     * Create a new MedPass workflow
     */
    async createWorkflow(workflowData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/workflows`, workflowData);
            return {
                success: true,
                data: response.data,
                message: "MedPass workflow created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create MedPass workflow",
                data: null
            };
        }
    }

    /**
     * Get workflow by ID
     */
    async getWorkflowById(workflowId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/workflows/${workflowId}`);
            return {
                success: true,
                data: response.data,
                message: "Workflow fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch workflow",
                data: null
            };
        }
    }

    /**
     * List workflows with filtering and pagination
     */
    async listWorkflows(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/workflows`, { params });
            return {
                success: true,
                data: response.data || [],
                message: "Workflows fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch workflows",
                data: []
            };
        }
    }

    /**
     * Update workflow data
     */
    async updateWorkflow(workflowId, updateData) {
        try {
            const response = await apiClient.put(`${this.baseURL}/workflows/${workflowId}`, updateData);
            return {
                success: true,
                data: response.data,
                message: "Workflow updated successfully"
            };
        } catch (error) {
            console.error('Update workflow error:', error.response?.data);
            return {
                success: false,
                message: error.response?.data?.detail || error.response?.data?.message || "Failed to update workflow",
                data: null
            };
        }
    }

    /**
     * Delete workflow (soft delete)
     */
    async deleteWorkflow(workflowId) {
        try {
            const response = await apiClient.delete(`${this.baseURL}/workflows/${workflowId}`);
            return {
                success: true,
                data: response.data,
                message: "Workflow deleted successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to delete workflow",
                data: null
            };
        }
    }

    // ============================================================================
    // WORKFLOW STEP MANAGEMENT
    // ============================================================================

    /**
     * Complete a workflow step
     */
    async completeStep(workflowId, stepData) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/workflows/${workflowId}/complete-step`,
                stepData
            );
            return {
                success: true,
                data: response.data,
                message: "Step completed successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to complete step",
                data: null
            };
        }
    }

    /**
     * Validate workflow for completion and safety
     */
    async validateWorkflow(workflowId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/workflows/${workflowId}/validate`);
            return {
                success: true,
                data: response.data,
                message: "Workflow validation completed"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to validate workflow",
                data: null
            };
        }
    }

    /**
     * Run safety checks on workflow
     */
    async runSafetyChecks(workflowId) {
        try {
            const response = await apiClient.post(`${this.baseURL}/workflows/${workflowId}/safety-check`);
            return {
                success: true,
                data: response.data,
                message: "Safety checks completed"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to run safety checks",
                data: []
            };
        }
    }

    // ============================================================================
    // APPROVAL WORKFLOW
    // ============================================================================

    /**
     * Submit workflow for approval
     */
    async submitForApproval(workflowId, approvalData) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/workflows/${workflowId}/submit-for-approval`,
                approvalData
            );
            return {
                success: true,
                data: response.data,
                message: "Workflow submitted for approval"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to submit for approval",
                data: null
            };
        }
    }

    /**
     * Approve or reject workflow
     */
    async approveWorkflow(workflowId, approvalResponse) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/workflows/${workflowId}/approve`,
                approvalResponse
            );
            return {
                success: true,
                data: response.data,
                message: approvalResponse.approved ? "Workflow approved" : "Workflow rejected"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to process approval",
                data: null
            };
        }
    }

    // ============================================================================
    // ANALYTICS AND REPORTING
    // ============================================================================

    /**
     * Get workflow statistics
     */
    async getWorkflowStats() {
        try {
            const response = await apiClient.get(`${this.baseURL}/workflows/stats`);
            return {
                success: true,
                data: response.data,
                message: "Statistics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch statistics",
                data: null
            };
        }
    }

    // ============================================================================
    // TEMPLATES AND PRESETS
    // ============================================================================

    /**
     * Get condition templates for pre/post medication conditions
     */
    async getConditionTemplates(conditionType, medicationClass = null) {
        try {
            const params = { condition_type: conditionType };
            if (medicationClass) {
                params.medication_class = medicationClass;
            }
            
            const response = await apiClient.get(`${this.baseURL}/templates/conditions`, { params });
            return {
                success: true,
                data: response.data,
                message: "Condition templates fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch condition templates",
                data: {}
            };
        }
    }

    /**
     * Get scheduling templates for medication administration
     */
    async getSchedulingTemplates(frequency = null, administrationMethod = null) {
        try {
            const params = {};
            if (frequency) params.frequency = frequency;
            if (administrationMethod) params.administration_method = administrationMethod;
            
            const response = await apiClient.get(`${this.baseURL}/templates/scheduling`, { params });
            return {
                success: true,
                data: response.data,
                message: "Scheduling templates fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch scheduling templates",
                data: []
            };
        }
    }

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    /**
     * Get workflow step names for display
     */
    getWorkflowStepNames() {
        return {
            'patient_demographics': 'Patient Demographics',
            'medication_order_entry': 'Medication Order Entry',
            'pre_post_conditions': 'Pre/Post Conditions',
            'physician_notes_scheduling': 'Physician Notes & Scheduling'
        };
    }

    /**
     * Get workflow status names for display
     */
    getWorkflowStatusNames() {
        return {
            'draft': 'Draft',
            'in_progress': 'In Progress',
            'pending_review': 'Pending Review',
            'approved': 'Approved',
            'completed': 'Completed',
            'cancelled': 'Cancelled',
            'rejected': 'Rejected'
        };
    }

    /**
     * Get vital sign type names for display
     */
    getVitalSignTypes() {
        return {
            'blood_pressure': 'Blood Pressure',
            'heart_rate': 'Heart Rate',
            'temperature': 'Temperature',
            'respiratory_rate': 'Respiratory Rate',
            'oxygen_saturation': 'Oxygen Saturation',
            'blood_glucose': 'Blood Glucose',
            'weight': 'Weight',
            'height': 'Height',
            'pain_scale': 'Pain Scale (0-10)'
        };
    }

    /**
     * Get administration method names for display
     */
    getAdministrationMethods() {
        return {
            'self_administered': 'Self-Administered',
            'nurse_administered': 'Nurse-Administered',
            'family_assisted': 'Family-Assisted',
            'caregiver_assisted': 'Caregiver-Assisted'
        };
    }

    /**
     * Calculate workflow progress percentage
     */
    calculateProgress(workflow) {
        if (!workflow) return 0;
        
        let completed = 0;
        const total = 4;
        
        if (workflow.demographics_completed) completed++;
        if (workflow.medication_entry_completed) completed++;
        if (workflow.conditions_completed) completed++;
        if (workflow.clinical_documentation_completed) completed++;
        
        return Math.round((completed / total) * 100);
    }

    /**
     * Get next step in workflow
     */
    getNextStep(workflow) {
        if (!workflow) return null;
        
        if (!workflow.demographics_completed) {
            return 'patient_demographics';
        } else if (!workflow.medication_entry_completed) {
            return 'medication_order_entry';
        } else if (!workflow.conditions_completed) {
            return 'pre_post_conditions';
        } else if (!workflow.clinical_documentation_completed) {
            return 'physician_notes_scheduling';
        }
        
        return null;
    }

    /**
     * Check if workflow can be submitted for approval
     */
    canSubmitForApproval(workflow) {
        if (!workflow) return false;
        
        return (
            workflow.demographics_completed &&
            workflow.medication_entry_completed &&
            workflow.conditions_completed &&
            workflow.clinical_documentation_completed &&
            workflow.validation_errors?.length === 0 &&
            !workflow.submitted_for_approval
        );
    }

    /**
     * Format workflow summary for display
     */
    formatWorkflowSummary(workflow) {
        if (!workflow) return null;
        
        const stepNames = this.getWorkflowStepNames();
        const statusNames = this.getWorkflowStatusNames();
        
        return {
            id: workflow.workflow_id,
            patientName: workflow.patient_name || 'Unknown Patient',
            patientMRN: workflow.patient_mrn || 'Unknown MRN',
            currentStep: stepNames[workflow.current_step] || workflow.current_step,
            status: statusNames[workflow.status] || workflow.status,
            progress: workflow.progress_percentage || 0,
            medicationCount: workflow.medication_count || 0,
            hasSafetyAlerts: workflow.has_safety_alerts || false,
            hasValidationErrors: workflow.has_validation_errors || false,
            requiresApproval: workflow.requires_approval || false,
            createdAt: workflow.created_at,
            updatedAt: workflow.updated_at
        };
    }

    /**
     * Validate patient profile data
     */
    validatePatientProfile(profile) {
        const errors = [];
        
        if (!profile.full_name?.trim()) {
            errors.push('Patient name is required');
        }
        
        if (!profile.date_of_birth) {
            errors.push('Date of birth is required');
        }
        
        if (!profile.mrn?.trim()) {
            errors.push('Medical Record Number is required');
        }
        
        if (!profile.gender?.trim()) {
            errors.push('Gender is required');
        }
        
        if (profile.age && (profile.age < 0 || profile.age > 150)) {
            errors.push('Please enter a valid age');
        }
        
        if (profile.email && !profile.email.includes('@')) {
            errors.push('Please enter a valid email address');
        }
        
        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Validate medication entry data
     */
    validateMedicationEntry(medications) {
        const errors = [];
        
        if (!medications || medications.length === 0) {
            errors.push('At least one medication is required');
        }
        
        medications?.forEach((med, index) => {
            if (!med.medication_id) {
                errors.push(`Medication ${index + 1}: Please select a medication`);
            }
            
            if (!med.dose_amount?.trim()) {
                errors.push(`Medication ${index + 1}: Dose amount is required`);
            }
            
            if (!med.frequency?.trim()) {
                errors.push(`Medication ${index + 1}: Frequency is required`);
            }
            
            if (!med.directions?.trim()) {
                errors.push(`Medication ${index + 1}: Directions are required`);
            }
            
            if (!med.quantity || med.quantity <= 0) {
                errors.push(`Medication ${index + 1}: Valid quantity is required`);
            }
        });
        
        return {
            isValid: errors.length === 0,
            errors
        };
    }

    // ============================================================================
    // PATIENT-CONTEXT AWARE METHODS
    // ============================================================================

    /**
     * Create patient-context aware MedPass workflow
     */
    async createPatientContextWorkflow(workflowData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/patient-context/workflows`, workflowData);
            return {
                success: true,
                data: response.data,
                message: "Patient-context MedPass workflow created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create patient-context workflow",
                data: null
            };
        }
    }

    /**
     * Get patient-context workflow by ID
     */
    async getPatientContextWorkflow(workflowId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/workflows/${workflowId}`);
            return {
                success: true,
                data: response.data,
                message: "Workflow fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch workflow",
                data: null
            };
        }
    }

    /**
     * Update patient-context workflow
     */
    async updatePatientContextWorkflow(workflowId, updateData) {
        try {
            const response = await apiClient.put(`${this.baseURL}/patient-context/workflows/${workflowId}`, updateData);
            return {
                success: true,
                data: response.data,
                message: "Workflow updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update workflow",
                data: null
            };
        }
    }

    /**
     * Submit patient-context workflow for processing
     */
    async submitPatientContextWorkflow(workflowId, submissionData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/patient-context/workflows/${workflowId}/submit`, submissionData);
            return {
                success: true,
                data: response.data,
                message: "Workflow submitted successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to submit workflow",
                data: null
            };
        }
    }

    /**
     * Get active workflows for a patient
     */
    async getPatientActiveWorkflows(patientId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/patient-context/patients/${patientId}/active-workflows`);
            return {
                success: true,
                data: response.data,
                message: "Active workflows retrieved successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to retrieve active workflows",
                data: []
            };
        }
    }

    /**
     * Get patient medications from completed workflows
     */
    async getPatientMedications(patientId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/patient-context/patients/${patientId}/medications`);
            return {
                success: true,
                data: response.data,
                message: "Patient medications retrieved successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to retrieve patient medications",
                data: []
            };
        }
    }

    /**
     * Get patient medication history
     */
    async getPatientMedicationHistory(patientId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/patient-context/patients/${patientId}/medication-history`);
            return {
                success: true,
                data: response.data,
                message: "Medication history retrieved successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to retrieve medication history",
                data: []
            };
        }
    }

    /**
     * Get safety alerts for a patient
     */
    async getPatientSafetyAlerts(patientId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/patient-context/patients/${patientId}/safety-alerts`);
            return {
                success: true,
                data: response.data,
                message: "Safety alerts retrieved successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to retrieve safety alerts",
                data: []
            };
        }
    }

    /**
     * Search DOH-approved medications with patient-specific filtering
     */
    async searchDOHApprovedMedications(searchParams) {
        try {
            const response = await apiClient.get(`${this.baseURL}/patient-context/medications/search-doh`, {
                params: searchParams
            });
            return {
                success: true,
                data: response.data,
                message: "DOH medications retrieved successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to search DOH medications",
                data: []
            };
        }
    }

    /**
     * Run comprehensive medication safety checks
     */
    async runMedicationSafetyChecks(safetyCheckData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/patient-context/safety-checks`, safetyCheckData);
            return {
                success: true,
                data: response.data,
                message: "Safety checks completed successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to run safety checks",
                data: {
                    drug_interactions: [],
                    allergy_conflicts: [],
                    contraindications: [],
                    overall_safe: false
                }
            };
        }
    }

    /**
     * Validate patient-context workflow before submission
     */
    async validatePatientContextWorkflow(workflowId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/patient-context/workflows/${workflowId}/validate`);
            return {
                success: true,
                data: response.data,
                message: "Workflow validation completed"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to validate workflow",
                data: {
                    is_valid: false,
                    errors: ["Validation failed"]
                }
            };
        }
    }

    /**
     * Log workflow events for audit trail
     */
    async logWorkflowEvent(workflowId, eventData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/patient-context/workflows/${workflowId}/log-event`, eventData);
            return {
                success: true,
                data: response.data,
                message: "Event logged successfully"
            };
        } catch (error) {
            console.error('Failed to log workflow event:', error);
            return {
                success: false,
                message: "Failed to log event",
                data: null
            };
        }
    }

    // ============================================================================
    // MEDICATION ADMINISTRATION METHODS
    // ============================================================================

    /**
     * Get scheduled medications for a patient with calculated due times
     */
    async getPatientScheduledMedications(patientId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/patient-context/patients/${patientId}/scheduled-medications`);
            return {
                success: true,
                data: response.data,
                message: "Scheduled medications retrieved successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to retrieve scheduled medications",
                data: []
            };
        }
    }

    /**
     * Record medication administration
     */
    async recordMedicationAdministration(administrationData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/administration/record`, administrationData);
            return {
                success: true,
                data: response.data,
                message: "Medication administration recorded successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to record medication administration",
                data: null
            };
        }
    }

    /**
     * Hold medication (temporarily suspend administration)
     */
    async holdMedication(workflowId, medicationId, reason = '') {
        try {
            const response = await apiClient.post(`${this.baseURL}/medications/hold`, {
                workflow_id: workflowId,
                medication_id: medicationId,
                hold_reason: reason
            });
            return {
                success: true,
                data: response.data,
                message: "Medication placed on hold successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to hold medication",
                data: null
            };
        }
    }

    /**
     * Resume held medication
     */
    async resumeMedication(workflowId, medicationId) {
        try {
            const response = await apiClient.post(`${this.baseURL}/medications/resume`, {
                workflow_id: workflowId,
                medication_id: medicationId
            });
            return {
                success: true,
                data: response.data,
                message: "Medication resumed successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to resume medication",
                data: null
            };
        }
    }

    /**
     * Get medication administration history for a patient
     */
    async getMedicationAdministrationHistory(patientId, startDate = null, endDate = null) {
        try {
            const params = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const response = await apiClient.get(`${this.baseURL}/administration/history/${patientId}`, { params });
            return {
                success: true,
                data: response.data,
                message: "Administration history retrieved successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to retrieve administration history",
                data: []
            };
        }
    }

    /**
     * Get missed medications for a patient
     */
    async getMissedMedications(patientId, dateRange = 'today') {
        try {
            const response = await apiClient.get(`${this.baseURL}/patient-context/patients/${patientId}/missed-medications`, {
                params: { date_range: dateRange }
            });
            return {
                success: true,
                data: response.data,
                message: "Missed medications retrieved successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to retrieve missed medications",
                data: []
            };
        }
    }

    /**
     * Mark medication as refused by patient
     */
    async recordMedicationRefusal(workflowId, medicationId, refusalData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/administration/refuse`, {
                workflow_id: workflowId,
                medication_id: medicationId,
                ...refusalData
            });
            return {
                success: true,
                data: response.data,
                message: "Medication refusal recorded successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to record medication refusal",
                data: null
            };
        }
    }

    /**
     * Approve a patient-context workflow
     */
    async approvePatientContextWorkflow(workflowId) {
        try {
            const response = await apiClient.post(`${this.baseURL}/patient-context/workflows/${workflowId}/approve`);
            return {
                success: true,
                data: response.data,
                message: "Medication order approved successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to approve medication order",
                data: null
            };
        }
    }

    // ============================================================================
    // UTILITY METHODS FOR PATIENT-CONTEXT WORKFLOWS
    // ============================================================================

    /**
     * Calculate medication quantity based on dosing parameters
     */
    calculateMedicationQuantity(doseAmount, frequency, durationDays) {
        const frequencyMap = {
            'once_daily': 1,
            'twice_daily': 2,
            'three_times_daily': 3,
            'four_times_daily': 4,
            'every_4_hours': 6,
            'every_6_hours': 4,
            'every_8_hours': 3,
            'every_12_hours': 2,
            'with_meals': 3,
            'at_bedtime': 1,
            'as_needed': null // PRN - no automatic calculation
        };

        const dailyDoses = frequencyMap[frequency];
        if (!dailyDoses || !doseAmount || !durationDays) {
            return 0;
        }

        const totalDoses = dailyDoses * durationDays;
        const quantity = totalDoses * parseFloat(doseAmount);
        
        return Math.ceil(quantity);
    }

    /**
     * Generate physician instruction template
     */
    generateInstructionTemplate(medicationName, doseAmount, frequency, duration, specialNotes = '') {
        const frequencyText = {
            'once_daily': 'once daily',
            'twice_daily': 'twice daily',
            'three_times_daily': 'three times daily',
            'four_times_daily': 'four times daily',
            'every_4_hours': 'every 4 hours',
            'every_6_hours': 'every 6 hours',
            'every_8_hours': 'every 8 hours',
            'every_12_hours': 'every 12 hours',
            'with_meals': 'with meals',
            'at_bedtime': 'at bedtime',
            'as_needed': 'as needed'
        };

        let instruction = `Take ${doseAmount} ${frequencyText[frequency] || frequency}`;
        
        if (duration && duration !== 'as_needed') {
            instruction += ` for ${duration}`;
        }

        if (specialNotes) {
            instruction += `. ${specialNotes}`;
        }

        return instruction;
    }

    /**
     * Validate patient-context workflow step data
     */
    validatePatientContextStep(stepKey, stepData) {
        const errors = [];

        switch (stepKey) {
            case 'patient_context':
                if (!stepData.verified) {
                    errors.push('Patient identity must be verified');
                }
                if (!stepData.patient_id) {
                    errors.push('Patient ID is required');
                }
                break;

            case 'selected_medications':
                if (!stepData || stepData.length === 0) {
                    errors.push('At least one medication must be selected');
                }
                break;

            case 'dosing_instructions':
                if (!stepData.medication_instructions || stepData.medication_instructions.length === 0) {
                    errors.push('Dosing instructions are required for all medications');
                }
                stepData.medication_instructions?.forEach((instruction, index) => {
                    if (!instruction.physician_instructions?.trim()) {
                        errors.push(`Medication ${index + 1}: Physician instructions are required`);
                    }
                    if (!instruction.dose_amount) {
                        errors.push(`Medication ${index + 1}: Dose amount is required`);
                    }
                    if (!instruction.frequency) {
                        errors.push(`Medication ${index + 1}: Frequency is required`);
                    }
                });
                break;

            case 'monitoring_requirements':
                // Monitoring is optional, but if enabled, validate requirements
                if (stepData.monitoring_enabled) {
                    if (!stepData.monitoring_frequency) {
                        errors.push('Monitoring frequency is required when monitoring is enabled');
                    }
                }
                break;
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    // ============================================================================
    // SMART MEDICATION DASHBOARD - ENHANCED FUNCTIONALITY
    // ============================================================================

    /**
     * Get smart medication dashboard with 24h visibility window
     * Shows unadministered medications up to grace period + upcoming medications
     */
    async getMedicationDashboard(patientId) {
        try {
            const response = await apiClient.get(
                `${this.baseURL}/patient-context/patients/${patientId}/medications-dashboard`
            );
            return {
                success: true,
                data: response.data,
                message: "Smart medication dashboard loaded successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to load medication dashboard",
                data: null
            };
        }
    }

    /**
     * Search patients for medication dashboard
     */
    async searchPatients(query, limit = 10) {
        try {
            const response = await apiClient.get('/patients/search', {
                params: { q: query, limit }
            });
            return {
                success: true,
                data: response.data.patients || response.data || [],
                message: "Patients searched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to search patients",
                data: []
            };
        }
    }

    /**
     * Enhanced administration session end with status calculation and justification
     */
    async endAdministrationWithStatus(sessionId, administrationData) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/administration-sessions/${sessionId}/end`,
                {
                    ...administrationData,
                    // Include enhanced fields for status calculation
                    administration_time: administrationData.administration_time,
                    justification: administrationData.justification,
                    administration_notes: administrationData.administration_notes
                }
            );
            return {
                success: true,
                data: response.data,
                message: "Administration completed successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to complete administration",
                data: null
            };
        }
    }

    /**
     * Enhanced administration session start with early administration support
     */
    async startAdministrationWithJustification(sessionId, startData) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/administration-sessions/${sessionId}/start`,
                {
                    ...startData,
                    // Include justification for early administrations
                    justification: startData.justification
                }
            );
            return {
                success: true,
                data: response.data,
                message: "Administration started successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to start administration",
                data: null
            };
        }
    }

    /**
     * Get patient scheduled medications (legacy support)
     */
    async getPatientScheduledMedications(patientId) {
        try {
            const response = await apiClient.get(
                `${this.baseURL}/patient-context/patients/${patientId}/scheduled-medications`
            );
            return {
                success: true,
                data: response.data || [],
                message: "Scheduled medications fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch scheduled medications",
                data: []
            };
        }
    }

    /**
     * Get administration history for a patient
     */
    async getAdministrationHistory(patientId, limit = 50) {
        try {
            const response = await apiClient.get(
                `${this.baseURL}/administration/history/${patientId}`,
                { params: { limit } }
            );
            return {
                success: true,
                data: response.data || [],
                message: "Administration history fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch administration history",
                data: []
            };
        }
    }

    /**
     * Create administration session for smart dashboard
     */
    async createAdministrationSession(sessionData) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/administration-sessions`,
                sessionData
            );
            return {
                success: true,
                data: response.data,
                message: "Administration session created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create administration session",
                data: null
            };
        }
    }

    /**
     * Get administration sessions for a patient/medication
     */
    async getAdministrationSessions(params = {}) {
        try {
            const response = await apiClient.get(
                `${this.baseURL}/administration-sessions`,
                { params }
            );
            return {
                success: true,
                data: response.data || { sessions: [], total_count: 0 },
                message: "Administration sessions fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch administration sessions",
                data: { sessions: [], total_count: 0 }
            };
        }
    }
}

// Export service instance
const medpassService = new MedPassService();
export default medpassService;